import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { Project } from '../../types';
import TranscriptionReviewModal from './TranscriptionReviewModal';

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projects: Project[];
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  open,
  onClose,
  onSuccess,
  projects
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [transcriptionModalOpen, setTranscriptionModalOpen] = useState(false);
  const [uploadedJobId, setUploadedJobId] = useState<string | null>(null);

  const handleFileSelect = (selectedFile: File) => {
    const allowedTypes = [
      'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
      'text/plain',
      'audio/mpeg',
      'audio/wav',
      'audio/mp4',
      'audio/x-m4a'
    ];
    const maxSize = 50 * 1024 * 1024; // 50MB for audio files

    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Please select a PDF, Word document, text file, or audio file (MP3, WAV, M4A)');
      return;
    }

    if (selectedFile.size > maxSize) {
      setError('File size must be less than 50MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Job name is required');
      return;
    }

    if (!file) {
      setError('Please select a file');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await apiService.uploadDocument({
        name: name.trim(),
        description: description.trim() || undefined,
        projectId: projectId || undefined,
        file
      });

      // Check if it's an audio file and show transcription review
      const isAudioFile = file.type.startsWith('audio/') || 
                         file.name.toLowerCase().endsWith('.mp3') ||
                         file.name.toLowerCase().endsWith('.wav') ||
                         file.name.toLowerCase().endsWith('.m4a');

      if (isAudioFile && result?.id) {
        setUploadedJobId(result.id);
        setTranscriptionModalOpen(true);
      } else {
        onSuccess();
        handleClose();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setDescription('');
      setProjectId('');
      setFile(null);
      setError(null);
      onClose();
    }
  };

  const getFileTypeName = (file: File) => {
    switch (file.type) {
      case 'application/pdf':
        return 'PDF Document';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'Word Document';
      case 'text/plain':
        return 'Text File';
      case 'audio/mpeg':
        return 'MP3 Audio';
      case 'audio/wav':
        return 'WAV Audio';
      case 'audio/mp4':
      case 'audio/x-m4a':
        return 'M4A Audio';
      default:
        return 'Unknown File Type';
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document for Story Generation</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Job Name *"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              disabled={loading}
            />

            <TextField
              label="Description"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={loading}
            />

            <FormControl fullWidth margin="normal" disabled={loading}>
              <InputLabel>Project (Optional)</InputLabel>
              <Select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                label="Project (Optional)"
              >
                <MenuItem value="">
                  <em>No Project</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Document
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Supported formats: PDF, Word (.docx), Text (.txt), Audio (MP3, WAV, M4A)
            </Typography>

            <Paper
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: dragActive ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,.docx,.txt,.mp3,.wav,.m4a"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                disabled={loading}
              />

              {file ? (
                <Box>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getFileTypeName(file)} • {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    disabled={loading}
                  >
                    Change File
                  </Button>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Drop your document here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse files
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !name.trim() || !file}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transcription Review Modal */}
      <TranscriptionReviewModal
        open={transcriptionModalOpen}
        onClose={() => {
          setTranscriptionModalOpen(false);
          setUploadedJobId(null);
        }}
        jobId={uploadedJobId || ''}
        onProceed={() => {
          setTranscriptionModalOpen(false);
          setUploadedJobId(null);
          onSuccess();
          handleClose();
        }}
      />
    </>
  );
};

export default UploadDocumentModal;
